import { useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Radio,
  Lock,
  ShieldAlert,
} from 'lucide-react';
import { UserRole } from '@pms/shared';
import { useAuth } from '@/hooks/useAuth';
import {
  useMyPropertyListing,
  useMyAmenityCatalogue,
  useMyPropertyAuditLatest,
} from '@/hooks/api/useMyProperty';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { OverviewTab } from '@/components/admin/property-editor/OverviewTab';
import { GuidelinesTab } from '@/components/admin/property-editor/GuidelinesTab';
import { RoomsTab } from '@/components/admin/property-editor/RoomsTab';
import { AmenitiesTab } from '@/components/admin/property-editor/AmenitiesTab';
import { PhotosTab } from '@/components/admin/property-editor/PhotosTab';
import { MyPropertyEditorApiProvider } from '@/components/admin/property-editor/api-context';

/** The 5 hotel-editable tabs — NO Contacts/KYC, NO Publish (BestAcco-only). */
type TabKey = 'overview' | 'guidelines' | 'rooms' | 'amenities' | 'photos';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'guidelines', label: 'Guidelines & FAQ' },
  { key: 'rooms', label: 'Rooms & rates' },
  { key: 'amenities', label: 'Amenities' },
  { key: 'photos', label: 'Photos & video' },
];

const TAB_KEYS = TABS.map((t) => t.key);

/** Relative "time ago" for the "Last edited by …" header sub-line. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Hotel Property Listing Editor (`/properties/:id/listing`) — the tenant-scoped
 * self-service twin of the super-admin `AdminPropertyEditorPage`. Reuses the same
 * tab components through the `MyPropertyEditorApiProvider` (which points them at
 * `/v1/my-property/*`), renders only the 5 hotel tabs, and enforces client-side
 * role gating as UX (the server is the real guard):
 *  - ADMIN / OWNER → full write.
 *  - REVENUE_MANAGER → read-only (disabled fieldset + banner).
 *  - OPERATIONS / cross-tenant → server 403/404 → not-authorized state (F9).
 */
export function PropertyListingEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const role = user?.role;
  const canWrite = role === UserRole.ADMIN || role === UserRole.OWNER;

  const rawTab = searchParams.get('tab');
  const activeTab: TabKey = TAB_KEYS.includes(rawTab as TabKey) ? (rawTab as TabKey) : 'overview';

  const { data, isLoading, isError, error, refetch } = useMyPropertyListing(id ?? null);
  const { data: catalogue } = useMyAmenityCatalogue();
  const { data: audit } = useMyPropertyAuditLatest(id ?? null);

  const setTab = useCallback(
    (key: TabKey) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      });
    },
    [setSearchParams],
  );

  const onTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    else return;
    e.preventDefault();
    setTab(TABS[next].key);
    tabRefs.current[next]?.focus();
  };

  const status = (error as { response?: { status?: number } })?.response?.status;
  // F9: cross-tenant / no-access — the server returns 403 or 404; treat both as
  // "not authorized" and never render listing data.
  const notAuthorized = isError && (status === 403 || status === 404);

  return (
    <MyPropertyEditorApiProvider canWrite={canWrite}>
      <div className="space-y-6">
        <div>
          <button
            type="button"
            onClick={() => navigate('/properties')}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
          >
            <ArrowLeft size={15} aria-hidden="true" />
            Properties
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading property…</span>
          </div>
        ) : notAuthorized ? (
          <div className="rounded-xl border border-gray-200 bg-white">
            <EmptyState
              icon={ShieldAlert}
              title="You don't have access to this property"
              description="It may belong to another organisation, or you may not have permission to view it."
              actionLabel="Back to properties"
              onAction={() => navigate('/properties')}
            />
          </div>
        ) : isError || !data ? (
          <div className="rounded-xl border border-gray-200 bg-white py-16 text-center">
            <AlertCircle size={24} className="mx-auto mb-3 text-red-500" aria-hidden="true" />
            <p className="mb-4 text-sm text-red-700">Unable to load this property.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{data.property.name}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {data.property.city}
                  {data.property.country ? `, ${data.property.country}` : ''}
                </p>
                {audit && (
                  <p className="mt-1 text-xs text-gray-400">
                    Last edited by {audit.actorEmail} · {relativeTime(audit.createdAt)}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold',
                    data.publishGate.isLive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800',
                  )}
                >
                  <Radio size={14} aria-hidden="true" />
                  {data.publishGate.isLive ? 'Live' : 'Draft'}
                </span>
                {!data.publishGate.isLive && (
                  <p className="max-w-[15rem] text-right text-xs text-gray-400">
                    Saved changes go public once BestAcco approves the listing.
                  </p>
                )}
              </div>
            </div>

            {!canWrite && (
              <div
                className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
                role="status"
              >
                <Lock size={16} className="mt-0.5 shrink-0 text-blue-600" aria-hidden="true" />
                <p className="text-sm text-blue-800">
                  Read-only — ask an Administrator to edit the listing.
                </p>
              </div>
            )}

            <div className="border-b border-gray-200">
              <div role="tablist" aria-label="Property editor sections" className="-mb-px flex flex-wrap gap-1 overflow-x-auto">
                {TABS.map((tab, index) => {
                  const selected = tab.key === activeTab;
                  return (
                    <button
                      key={tab.key}
                      ref={(el) => {
                        tabRefs.current[index] = el;
                      }}
                      role="tab"
                      id={`tab-${tab.key}`}
                      aria-selected={selected}
                      aria-controls={`panel-${tab.key}`}
                      tabIndex={selected ? 0 : -1}
                      onClick={() => setTab(tab.key)}
                      onKeyDown={(e) => onTabKeyDown(e, index)}
                      className={cn(
                        'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-t',
                        selected
                          ? 'border-brand-600 text-brand-700'
                          : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300',
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* F10: a disabled <fieldset> natively disables every descendant
                control (inputs, selects, buttons, Save), so a read-only role needs
                no per-tab branching. */}
            <fieldset disabled={!canWrite} className="m-0 min-w-0 border-0 p-0">
              <div role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`} tabIndex={0} className="focus-visible:outline-none">
                {activeTab === 'overview' && <OverviewTab property={data.property} />}
                {activeTab === 'guidelines' && (
                  <GuidelinesTab propertyId={data.property.id} profile={data.profile} hideInternal />
                )}
                {activeTab === 'rooms' && (
                  <RoomsTab
                    propertyId={data.property.id}
                    units={data.units}
                    currency={data.property.currency}
                    publishGate={data.publishGate}
                  />
                )}
                {activeTab === 'amenities' && (
                  <AmenitiesTab
                    propertyId={data.property.id}
                    catalogue={catalogue ?? data.amenities.catalogue}
                    selected={data.amenities.selected}
                  />
                )}
                {activeTab === 'photos' && (
                  <PhotosTab propertyId={data.property.id} media={data.media} />
                )}
              </div>
            </fieldset>
          </>
        )}
      </div>
    </MyPropertyEditorApiProvider>
  );
}
